import { Button, useConfirm } from "@ml/ui";
import { useEffect, useRef } from "react";
import { useBlocker } from "react-router";

/**
 * Warn before leaving with unsaved edits: in-app navigation shows a confirm,
 * closing/reloading the tab shows the browser prompt.
 */
export function useUnsavedChangesGuard(dirty: boolean) {
  const confirm = useConfirm();
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  );

  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    void confirm({
      title: "Leave without saving?",
      description: "You have changes on this page that haven't been saved.",
      confirmLabel: "Discard changes",
      cancelLabel: "Keep editing",
      tone: "danger",
    }).then((ok) => {
      const b = blockerRef.current;
      if (b.state !== "blocked") return;
      if (ok) b.proceed();
      else b.reset();
    });
    // Only react to the transition into "blocked".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
}

/** Sticky "unsaved changes" bar. Render it at the end of a settings form. */
export function SaveBar({
  dirty,
  saving,
  onSave,
  onDiscard,
  message = "You have unsaved changes.",
  saveLabel = "Save changes",
  disabled,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  message?: string;
  saveLabel?: string;
  disabled?: boolean;
}) {
  return (
    <div className={`gfp-savebar${dirty ? " gfp-savebar--show" : ""}`} aria-hidden={!dirty} role="region" aria-label="Unsaved changes">
      <span className="gfp-savebar__msg">{message}</span>
      <div className="gfp-savebar__actions">
        <Button variant="ghost" onClick={onDiscard} disabled={saving || !dirty} tabIndex={dirty ? 0 : -1}>
          Discard
        </Button>
        <Button variant="primary" onClick={onSave} loading={saving} disabled={disabled || !dirty} tabIndex={dirty ? 0 : -1}>
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}
